import React from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { XCircle, RefreshCw, Home, Phone, Mail } from 'lucide-react';

const BookingFailurePage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const bookingId = searchParams.get('booking');
  const error = searchParams.get('error') || 'Payment failed';

  const handleRetry = () => {
    // Navigate back to homepage to restart booking
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-gray-950 to-black py-20">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Failure Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-24 h-24 bg-red-500 rounded-full mb-6">
            <XCircle size={48} className="text-white" />
          </div>
          <h1 className="text-4xl md:text-5xl font-black text-white mb-4">
            Payment Failed
          </h1>
          <p className="text-xl text-gray-300">
            We couldn't process your payment. Please try again.
          </p>
        </div>

        {/* Error Details Card */}
        <div className="bg-gray-800 border-2 border-red-500 rounded-2xl overflow-hidden mb-8">
          <div className="bg-gradient-to-r from-red-600 to-red-500 p-6">
            <h2 className="text-2xl font-bold text-white">What Happened?</h2>
          </div>

          <div className="p-6 space-y-6">
            {/* Error Message */}
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
              <p className="text-red-300 text-sm">
                <span className="font-bold">Error:</span> {decodeURIComponent(error)}
              </p>
            </div>

            {bookingId && (
              <div className="text-gray-300">
                <p className="text-sm">
                  <span className="text-gray-400">Booking Reference:</span>{' '}
                  <span className="text-white font-mono">{bookingId}</span>
                </p>
                <p className="text-xs text-gray-500 mt-2">
                  Your booking was created but payment failed. You can retry the payment.
                </p>
              </div>
            )}

            {/* Common Reasons */}
            <div>
              <h3 className="text-lg font-bold text-yellow-400 mb-3">Common Reasons for Payment Failure</h3>
              <ul className="space-y-2 text-gray-300 text-sm">
                <li className="flex items-start">
                  <span className="text-yellow-400 mr-2">•</span>
                  <span>Insufficient balance in your account</span>
                </li>
                <li className="flex items-start">
                  <span className="text-yellow-400 mr-2">•</span>
                  <span>Incorrect card details or CVV</span>
                </li>
                <li className="flex items-start">
                  <span className="text-yellow-400 mr-2">•</span>
                  <span>Card not enabled for online transactions</span>
                </li>
                <li className="flex items-start">
                  <span className="text-yellow-400 mr-2">•</span>
                  <span>Transaction limit exceeded</span>
                </li>
                <li className="flex items-start">
                  <span className="text-yellow-400 mr-2">•</span>
                  <span>Network timeout or connectivity issues</span>
                </li>
              </ul>
            </div>

            {/* What to Do */}
            <div>
              <h3 className="text-lg font-bold text-yellow-400 mb-3">What You Can Do</h3>
              <div className="space-y-3">
                <div className="bg-gray-700 rounded-lg p-4 text-sm text-gray-300">
                  <p className="font-semibold text-white mb-2">1. Check Your Payment Details</p>
                  <p>Ensure your card is enabled for online transactions and has sufficient balance</p>
                </div>
                <div className="bg-gray-700 rounded-lg p-4 text-sm text-gray-300">
                  <p className="font-semibold text-white mb-2">2. Try a Different Payment Method</p>
                  <p>Use another card, UPI, or net banking option</p>
                </div>
                <div className="bg-gray-700 rounded-lg p-4 text-sm text-gray-300">
                  <p className="font-semibold text-white mb-2">3. Contact Your Bank</p>
                  <p>If the issue persists, contact your bank to enable online transactions</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
          <button
            onClick={handleRetry}
            className="flex items-center justify-center px-8 py-4 bg-yellow-400 hover:bg-yellow-500 text-black font-bold rounded-lg transition-colors"
          >
            <RefreshCw size={20} className="mr-2" />
            Try Again
          </button>
          <Link
            to="/"
            className="flex items-center justify-center px-8 py-4 bg-gray-800 hover:bg-gray-700 text-white font-bold rounded-lg transition-colors border-2 border-gray-700"
          >
            <Home size={20} className="mr-2" />
            Return to Homepage
          </Link>
        </div>

        {/* Contact Support */}
        <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-6">
          <h3 className="text-lg font-bold text-white mb-4 text-center">Need Assistance?</h3>
          <div className="grid sm:grid-cols-2 gap-4 text-center">
            <div>
              <Phone className="mx-auto mb-2 text-blue-400" size={24} />
              <p className="text-white font-semibold">Call Us</p>
              <a href="tel:9876543210" className="text-blue-400 hover:text-blue-300">
                98765 43210
              </a>
            </div>
            <div>
              <Mail className="mx-auto mb-2 text-blue-400" size={24} />
              <p className="text-white font-semibold">Email Us</p>
              <a href="mailto:support@indyabungy.com" className="text-blue-400 hover:text-blue-300">
                support@indyabungy.com
              </a>
            </div>
          </div>
        </div>

        {/* Footer Note */}
        <div className="text-center mt-8 text-gray-400 text-sm">
          <p>If money was deducted from your account, it will be refunded within 5-7 business days</p>
        </div>
      </div>
    </div>
  );
};

export default BookingFailurePage;
